/**
 * Per-model token rates for Distro AI chat. All values are **USD per million
 * tokens** — e.g. a value of `3` means `$3.00 per 1,000,000 tokens`.
 *
 * Lives in @aidkitorg/types so both the backend cost estimator and the
 * frontend model picker read the same table.
 *
 * - `freshInput`: fresh input tokens (not from cache).
 * - `cacheRead`: cached input tokens (read).
 * - `cacheWrite`: cached input tokens (write).
 *   - Anthropic: we configure 1h TTL on the system prompt, so writes are 2× base.
 *   - OpenAI: GPT-5.6+ writes carry a 1.25× surcharge (the SDK does not report
 *     OpenAI cache writes, so this term is currently always 0 for OpenAI).
 * - `output`: completion tokens.
 *
 * Verified against current published rates at
 * https://platform.claude.com/docs/en/about-claude/pricing and
 * https://platform.openai.com/docs/pricing.
 *
 * NOTE: hardcoded rates are a short-term fix for cost visibility while rolling
 * out this feature. When this needs to scale (more LLM-powered features, real
 * billing, per-program overrides, or rate updates without a deploy), move
 * these to an external config (S3 / Parameter Store / DB) so non-engineers
 * can update them.
 */
export type ModelRates = {
  freshInput: number;
  cacheRead: number;
  cacheWrite: number;
  output: number;
};

/** Structural subset of the AI SDK's LanguageModelUsage, so this package doesn't depend on 'ai'. */
export type ModelUsage = {
  inputTokens?: number;
  outputTokens?: number;
  inputTokenDetails?: {
    cacheReadTokens?: number;
    cacheWriteTokens?: number;
    noCacheTokens?: number;
  };
};

export const MODEL_USD_PER_MIL_TOKEN_RATES: Record<string, ModelRates> = {
  'claude-haiku-4-5':  { freshInput: 1.00, cacheRead: 0.10, cacheWrite: 2.00,  output: 5.00  },
  'claude-sonnet-5-5': { freshInput: 2.00, cacheRead: 0.20, cacheWrite: 4.00,  output: 10.00 },
  // Opus 5.5 cache reads are 0.05× base, not the usual 0.1×.
  'claude-opus-5-5':   { freshInput: 4.00, cacheRead: 0.20, cacheWrite: 8.00,  output: 20.00 },
  'gpt-6-luna':        { freshInput: 0.10, cacheRead: 0.01, cacheWrite: 0.125, output: 0.50  },
  'gpt-6-sol':         { freshInput: 2.00, cacheRead: 0.20, cacheWrite: 2.50,  output: 10.00 },
};

/**
 * Compute the dollar cost of a single usage object for a given model. Returns
 * 0 for unknown models so callers can keep adding models without breaking
 * cost rollups.
 */
export function estimateCost(usage: ModelUsage, modelId: string): number {
  const rates = MODEL_USD_PER_MIL_TOKEN_RATES[modelId];
  if (!rates) return 0;

  const cacheRead = usage.inputTokenDetails?.cacheReadTokens ?? 0;
  const cacheWrite = usage.inputTokenDetails?.cacheWriteTokens ?? 0;
  // inputTokens is the *total* (including cached), so subtract cache reads/writes
  // when noCacheTokens isn't reported to avoid double-counting cached tokens.
  const fresh = usage.inputTokenDetails?.noCacheTokens
    ?? Math.max((usage.inputTokens ?? 0) - cacheRead - cacheWrite, 0);
  const output = usage.outputTokens ?? 0;

  // Rates are USD per 1M tokens; multiply tokens by rate then divide by 1M.
  return (
    (fresh * rates.freshInput) +
    (cacheRead * rates.cacheRead) +
    (cacheWrite * rates.cacheWrite) +
    (output * rates.output)
  ) / 1_000_000;
}
